import {
  useEffect,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import i18n from 'i18next';
import { Option } from 'react-select/src/filters';

import {
  selection,
} from 'redux-resource-plugins';

import {
  getStatus,
} from 'redux-resource';

import {
  ResourceType,
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  Icon,
  Button,
  Select,
  Spinner,
  Checkbox,
  ClientSearchOption,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import * as S from './styled';

type Props = {
  groupId: number,
  selectedIds: any,
  resourceType: ResourceType,
  onRemove: (id: number) => void;
};

const Members = ({
  groupId,
  resourceType,
  selectedIds,
  onRemove,
}: Props) => {
  const mapState = useCallback(state => ({
    clients: coreSelectors.getGroupItems(
      resourceType,
      groupId,
    )(state),
    searchOptions: coreSelectors.getSearchGroupOptions(
      groupId,
      resourceType,
    )(state),
    updateGroupEntityStatus: getStatus(
      state,
      [
        'clientGroups.requests.updateGroupEntity.status',
      ],
    ),
    addMemberToGroupStatus: getStatus(
      state,
      [
        'clientGroups.requests.addMemberToGroup.status',
      ],
    ),
  }), [groupId, resourceType]);

  const {
    clients,
    searchOptions,
    updateGroupEntityStatus,
    addMemberToGroupStatus,
  } = useSelector(mapState, shallowEqual);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      coreActions.requestGroupEntity(
        groupId,
        'clientGroups',
        'updateGroupEntity',
      ),
    );
  }, [groupId, resourceType]);

  const addToGroup = (clientId: string) => {
    dispatch(
      coreActions.requestAddToGroup(
        Number(clientId),
        Number(groupId),
      ),
    );
  };

  const onChecked = (id: number) => {
    if (selectedIds.includes(id)) {
      dispatch(selection.deselectResources('clients', [id]));
    } else {
      dispatch(selection.selectResources('clients', [id]));
    }
  };

  return (
    <>
      {updateGroupEntityStatus.succeeded && (
        <Flex
          column
          fillSpace
          width="100%"
        >
          <S.Search>
            <Select
              isClearable
              options={searchOptions}
              components={{ Option: ClientSearchOption }}
              placeholder="Add client to this group"
              onChange={(item: Option) => {
                if (item?.value) addToGroup(item.value);
              }}
              menuPosition="fixed"
            />
          </S.Search>

          {clients.length > 0 ? clients.map(member => (
            <S.Item
              key={member.id}
            >
              <Flex
                width="100%"
                align="center"
                justify="space-between"
              >
                <Flex
                  spacing={10}
                  align="center"
                >
                  <Checkbox
                    checked={member.selected}
                    onChange={() => onChecked(member.id)}
                  />
                  <S.ItemIcon
                    color={member.color}
                  >
                    {member.user_profile.avatar?.thumb?.url
                      ? (
                        <S.Avatar
                          src={member.user_profile.avatar.thumb.url}
                          alt=""
                        />
                      ) : (
                        <Icon
                          type="fa fa-user-circle"
                          size={28}
                        />
                      )}
                  </S.ItemIcon>
                  <S.Name
                    to={`/users/${member.id}/programs`}
                  >
                    {member.name || `${member.user_profile.first_name} ${member.user_profile.last_name}`}
                  </S.Name>
                </Flex>
                <Flex
                  align="center"
                  className="actions"
                  spacing={10}
                >
                  <Button
                    ghost
                    appearance="danger"
                    onClick={() => onRemove(member.id)}
                  >
                    Remove
                  </Button>
                </Flex>
              </Flex>
            </S.Item>
          )) : (
            <S.Empty>
              <S.EmptyIcon />
              <Text whiteSpace="pre-line">
                {i18n.t('client_groups.assigned.member.empty')}
              </Text>
            </S.Empty>
          )}

          {addMemberToGroupStatus.pending && (
            <Spinner
              over
              message="Adding Member..."
            />
          )}
        </Flex>
      )}

      {updateGroupEntityStatus.pending && (
        <Spinner
          message="Loading..."
        />
      )}
    </>
  );
};

export default Members;
